import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, Copy, CheckCircle2, ChevronRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Breadcrumbs from "@/components/layout/Breadcrumbs";

function ActivePromotion({ p }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!p.code) return;
    navigator.clipboard.writeText(p.code).then(() => {
      setCopied(true);
      toast.success(`Cod copiat: ${p.code}`);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const valueLabel = p.type === "percentage" ? `-${p.value}%` : `-${p.value} ${p.currency || "RON"}`;
  return (
    <div className="card-premium p-6 lg:p-7 flex flex-col" data-testid={`promo-card-${p.code || p.name}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--ct-gold)" }}>Promoție</span>
        <span className="font-serif text-3xl font-medium" style={{ color: "var(--ct-link)" }}>{valueLabel}</span>
      </div>
      <h3 className="font-serif text-2xl mb-2 leading-tight">{p.name}</h3>
      {p.description && (
        <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: "var(--ct-text-muted)" }}>{p.description}</p>
      )}
      {p.code ? (
        <div className="flex items-center gap-3 mt-auto">
          <button
            onClick={copy}
            className="flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-mono text-base hover:!border-[var(--ct-gold)] transition-colors"
            style={{ background: "var(--ct-bg-soft)", border: "1.5px dashed var(--ct-gold)" }}
            data-testid={`copy-${p.code}`}
          >
            <span style={{ color: "var(--ct-link)", fontWeight: 600, letterSpacing: "0.05em" }}>{p.code}</span>
            {copied ? <CheckCircle2 size={16} style={{ color: "var(--ct-link)" }} /> : <Copy size={14} style={{ color: "var(--ct-gold)" }} />}
          </button>
        </div>
      ) : (
        <p className="text-sm mt-auto" style={{ color: "var(--ct-text-muted)" }}>
          Reducere aplicată automat la checkout — nu ai nevoie de cod.
        </p>
      )}
      {p.valid_to && (
        <p className="text-[11px] mt-2 inline-flex items-center gap-1" style={{ color: "var(--ct-text-muted)" }}>
          <Calendar size={10} /> Valabil până {new Date(p.valid_to).toLocaleDateString("ro-RO")}
        </p>
      )}
    </div>
  );
}

export default function Promotions() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/promotions/active")
      .then((r) => setPromos(r.data || []))
      .catch(() => setPromos([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="promotions-page" className="py-12 lg:py-20" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="mb-6"><Breadcrumbs items={[{ label: "Promoții" }]} /></div>
        <div className="text-center mb-10">
          <span className="eyebrow">Reduceri active</span>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">Coduri & promoții</h1>
          <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: "var(--ct-text-muted)" }}>
            Aplică unul din codurile de mai jos la checkout pentru a obține reducerea. Unele coduri se aplică doar la anumite produse.
          </p>
        </div>

        {loading && <p className="text-center text-sm" style={{ color: "var(--ct-text-muted)" }}>Se încarcă...</p>}

        {!loading && promos.length === 0 && (
          <div className="card-premium p-10 text-center max-w-md mx-auto" data-testid="no-promos">
            <Tag size={36} className="mx-auto mb-4" style={{ color: "var(--ct-gold)" }} />
            <p className="text-sm" style={{ color: "var(--ct-text-muted)" }}>Nu există promoții active momentan. Abonează-te la newsletter pentru a fi notificat.</p>
            <Link to="/contact" className="btn-outline-gold mt-5 inline-flex">Abonează-mă <ChevronRight size={14} /></Link>
          </div>
        )}

        {!loading && promos.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="promotions-list">
            {promos.map((p) => <ActivePromotion key={p.code || p.id || p.name} p={p} />)}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-xs" style={{ color: "var(--ct-text-dim)" }}>
            * Codurile se aplică la checkout, când te înscrii la un <Link to="/cursuri" className="underline" style={{ color: "var(--ct-gold)" }}>curs</Link>. Doar un cod per comandă.
          </p>
        </div>
      </div>
    </div>
  );
}

// Sticky promotional banner — auto-shown at the top of the site when there's an active promotion.
export function PromoBanner() {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const d = localStorage.getItem("ct.promo.dismissed");
    if (d) setDismissed(true);
    api.get("/public/promotions/active")
      .then((r) => {
        const list = (r.data || []).filter((p) => p.published !== false);
        if (list.length > 0) setPromo(list[0]);
      })
      .catch(() => {});
  }, []);

  if (!promo || dismissed) return null;

  const valueLabel = promo.type === "percentage" ? `-${promo.value}%` : `-${promo.value} ${promo.currency || "RON"}`;

  const dismiss = () => {
    localStorage.setItem("ct.promo.dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="text-center py-2.5 px-4 text-xs sm:text-sm relative"
      style={{ background: "var(--ct-emerald)", color: "#F7F5F0" }}
      data-testid="promo-banner"
    >
      <Link to="/promotii" className="inline-flex items-center gap-2 hover:opacity-90">
        <Tag size={13} style={{ color: "var(--ct-gold)" }} />
        <span><strong>{valueLabel}</strong> · {promo.name}{promo.code ? <> · Cod: <code className="px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.15)", color: "var(--ct-gold)" }}>{promo.code}</code></> : null}</span>
        <ChevronRight size={12} />
      </Link>
      <button
        onClick={dismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100"
        aria-label="Închide"
        style={{ color: "var(--ct-gold)" }}
        data-testid="promo-banner-dismiss"
      >
        ×
      </button>
    </div>
  );
}
